import 'vite/modulepreload-polyfill';
import { ensurePromiseWithResolvers } from '@/common/promise-with-resolvers.polyfill';
import React, {useEffect, lazy, Suspense} from 'react';
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom';
import store from '@/store';
import { createRoot } from 'react-dom/client';
import {ConfigProvider, App as AntdApp, Spin} from 'antd';

ensurePromiseWithResolvers();

/*
 * Bootstrap is used for its reboot, grid and utility classes only. The full
 * bundle also ships ~90 kB of component CSS (buttons, cards, tables, modals,
 * navs, forms) that this app renders with antd instead. Reboot must stay: it
 * supplies the global border-box sizing and base typography.
 */
import 'bootstrap/dist/css/bootstrap-reboot.css'
import 'bootstrap/dist/css/bootstrap-grid.css'
import 'bootstrap/dist/css/bootstrap-utilities.css'
import '@/assets/less/site.less'
import '@/assets/less/site.media.less'
import '@/assets/less/antd.overrides.less';
import '@/assets/less/common.less';
import '@/assets/less/modals.less';
import '@/assets/less/popovers.less';
import '@/assets/less/upload-documents.less';
import '@/assets/less/drag-handler.less';
import '@/assets/less/dashboard-table.less';
import '@/assets/less/remixicon-fix.less';
import '@/theme/theme.less';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { haveAccessToAllSiteDashboard } from '@/pages/shared/shared.reducer';
import { getSetting, getUserPermissions } from '@/pages/login/login.reducer';
import { dashboardApi } from '@/pages/dashboard/dashboard.reducer';
import { BASE_USER, MANAGER, MANAGER_PLUS, CONTRIBUTOR, BASE_CHECKER, RESTRICTED_CONTRIBUTOR, SUPERVISOR, CONTRIBUTOR_PLUS } from '@/constants/roles';
import { setAntdAppInstances } from '@/notification';
import { installGlobalErrorHandlers, setClientErrorTransport, toClientTelemetryEvent } from '@/common/telemetry';
import { runLoginMutation } from '@/pages/login/login.query';
import config from '@/config';
import ReleaseNotificationModal from '@/pages/about/release-notification-modal';
import DmsThemeProvider from '@/theme/theme-provider';
import SystemOutageExperience from '@/common/system-outage/system-outage-experience';
import { PdfRestrictionsProvider } from '@/common/pdf-restrictions';

installGlobalErrorHandlers();
setClientErrorTransport((payload) => {
	if (!store.getState().userDetails.isLogin) return;
	runLoginMutation(store.dispatch, 'reportClientError', toClientTelemetryEvent(payload, config.VERSION))
		.catch(() => {});
});

import PrivateRoute from '@/private-route';
import { loadCartManagement, loadDistributionDashboard } from '@/common/route-preload';
import { getDefaultDashboardRoute } from '@/constants/default-dashboard-routes';
//import DashboardSkeleton from '@/common/skeleton/dashboard-skeleton';
//Main dashboards - eagerly imported to avoid Suspense flicker on first tab change
import DashboardContainer from '@/pages/dashboard';
import AllSitesDashboardContainer from '@/pages/all-sites-dashboard';
// Distribution dashboards and Cart Management are split out of the main bundle (AB#2926).
// DashboardChunkPreloader fetches the distribution chunk at startup only when it is the user's default dashboard.
const DistributionDashboard = lazy(loadDistributionDashboard);
const CartManagement = lazy(loadCartManagement);

//Summaries
const ApprovalSummary = lazy(() => import('@/pages/approval-summary'));
const CheckerSummary = lazy(() => import('@/pages/checker-summary'));
const JobSummary = lazy(() => import('@/pages/job-summary'));

//Utilities
const NumberTypeField = lazy(() => import( '@/pages/document-number-type-field-summary'));
const ExportMetadata = lazy(() => import( '@/pages/export-document'));
const ImportMetadata = lazy(() => import( '@/pages/import-metadata'));
const DeletedDocuments = lazy(() => import( '@/pages/deleted-documents'));
const CheckForLockedPdfs = lazy(() => import( '@/pages/check-for-locked-pdfs'));
const CheckFiles = lazy(() => import( '@/pages/check-files'));
const RenameBatchFile = lazy(() => import( '@/pages/rename/rename-batch-file'));
const DocumentNumberType = lazy(() => import( '@/pages/document-number-type'));
const NumberTypeGenerator = lazy(() => import( '@/pages/number-type-generator'));
const ReportComponent = lazy(() => import( '@/pages/reports'));
const NumberAvailabilityCheck = lazy(() => import( '@/pages/number-availability-check'));
const HelpPage = lazy(() => import( '@/pages/help-page'));
const CriticalEquipmentType = lazy(() => import( '@/pages/critical-equipment-types'));
const UpdateMetadata = lazy(() => import( '@/pages/update-metadata'));
const RenameDocument = lazy(() => import( '@/pages/rename/rename-document'));
const RevisionRollback = lazy(() => import( '@/pages/revision-rollback'));
const RebuildCategories = lazy(() => import( '@/pages/rebuild-categories'));
const SecretMigration = lazy(() => import( '@/pages/secret-migration'));

const UploadDocuments = lazy(() => import( '@/pages/upload-documents'));

const ReleaseNotes = lazy(() => import( '@/pages/about/release-notes'));
const Roadmap = lazy(() => import( '@/pages/about/roadmap'));
const ContactSupport = lazy(() => import( '@/pages/about/contact-support'));
const SuggestImprovement = lazy(() => import( '@/pages/about/suggest-improvement'));
const AboutDrawingManagers = lazy(() => import( '@/pages/about/about-drawing-managers'));
const PrivacyPolicy = lazy(() => import( '@/pages/about/privacy-policy'));
const TermsOfUse = lazy(() => import( '@/pages/about/terms-and-conditions'));
const MyProfile = lazy(() => import( '@/common/user-profile/user-profile-page'));
const StaffContainer = lazy(() => import( '@/pages/staff'));
const AccountsContainer = lazy(() => import( '@/pages/accounts'));
const LoginComponent = lazy(() => import( '@/pages/login'));
const LicenseComponent = lazy(() => import( '@/pages/license'));
const ErrorComponent = lazy(() => import( '@/pages/error-page'));
const FavouriteRevisionDocumentPage = lazy(() => import('@/pages/favourite-revisions'));



	const AntdAppBridge = () => {
		const { notification, modal } = AntdApp.useApp();

		useEffect(() => {
			setAntdAppInstances({
				notificationInstance: notification,
				modalInstance: modal,
			});
		}, [modal, notification]);

		return null;
	};

	// Users whose default dashboard is a distribution dashboard need that chunk at startup.
	// Everyone else downloads it only when they navigate there.
	const DashboardChunkPreloader = () => {
		const isLogin = useSelector(({ userDetails }) => userDetails?.isLogin);
		const defaultDashboardId = useSelector(({ userDetails }) => userDetails?.setting?.defaultDashboardId);

		useEffect(() => {
			if (isLogin && getDefaultDashboardRoute(defaultDashboardId).startsWith('/distribution-dashboard')) {
				loadDistributionDashboard().catch(() => {});
			}
		}, [isLogin, defaultDashboardId]);

		return null;
	};

	const AppRoutes = () => {
		const userDetails = store.getState().userDetails;

		const dispatch = useDispatch()
		useEffect(() => {
			const initializeApp = async () => {
				try {
					dispatch(getSetting());
					await dispatch(dashboardApi.endpoints.fetchSiteList.initiate()).unwrap();
					dispatch(dashboardApi.endpoints.fetchAccountDashboardSettings.initiate());
					dispatch(getUserPermissions());				
					dispatch(haveAccessToAllSiteDashboard());
				} catch (error) {
					console.error('Initialization failed:', error);
				}
			};
	
			if (userDetails.isLogin) {
				initializeApp();
			}
		}, [dispatch, userDetails.isLogin]);
 

		return (
			<>
				<DashboardChunkPreloader />
				<ReleaseNotificationModal />
				<Routes>
                    <Route path='/favourites/documents/:documentId' element={
                        <Suspense fallback={<Spin />}><FavouriteRevisionDocumentPage /></Suspense>
                    } />
					<Route index element={
						<PrivateRoute component={DashboardContainer} />
					} />

					<Route path='/all-sites-dashboard' element={
						<PrivateRoute component={AllSitesDashboardContainer} isAllSiteDashboard={true} />
					} />

					<Route path='/distribution-dashboard/:recordTypeId' element={
						<PrivateRoute component={DistributionDashboard} />
					} />

					<Route path='/critical-equipment-dashboard' element={<Navigate to='/distribution-dashboard/0' replace />} />
					<Route path='/standard-jobs-dashboard' element={<Navigate to='/distribution-dashboard/10' replace />} />
					<Route path='/books-dashboard' element={<Navigate to='/distribution-dashboard/20' replace />} />
					<Route path='/isolations-dashboard' element={<Navigate to='/distribution-dashboard/30' replace />} />
					<Route path='/safety-dashboard' element={<Navigate to='/distribution-dashboard/40' replace />} />

					<Route path='/approval-summary' element={
						<PrivateRoute component={ApprovalSummary} forbiddenRoles={[BASE_USER, BASE_CHECKER]} />
					} />

					<Route path='/checking-summary' element={
						<PrivateRoute component={CheckerSummary} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/update-metadata' element={
						<PrivateRoute
							component={UpdateMetadata}
							forbiddenRoles={[BASE_USER, BASE_CHECKER]}
							requiredSetting="canDoBulkMetadataUtilityEdits"
						/>
					} />

					<Route path='/job-summary' element={
						<PrivateRoute component={JobSummary} />
					} />

					<Route path='/standard-documents' element={
						<PrivateRoute component={DashboardContainer} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/reviewable-documents' element={
						<PrivateRoute component={DashboardContainer} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/inspection-reports' element={
						<PrivateRoute component={DashboardContainer} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/critical-equipment' element={
						<PrivateRoute component={DashboardContainer} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/standard-jobs' element={
						<PrivateRoute component={DashboardContainer} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/books' element={
						<PrivateRoute component={DashboardContainer} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/isolations' element={
						<PrivateRoute component={DashboardContainer} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/safety' element={
						<PrivateRoute component={DashboardContainer} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/export-account-contact' element={
						<PrivateRoute 
							component={DashboardContainer} 
							forbiddenRoles={[BASE_USER, BASE_CHECKER, RESTRICTED_CONTRIBUTOR, CONTRIBUTOR, CONTRIBUTOR_PLUS]} />
					} />

					<Route path='/export-metadata' element={
						<PrivateRoute component={ExportMetadata} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/import-metadata' element={
						<PrivateRoute 
							component={ImportMetadata} 
							forbiddenRoles={[MANAGER, CONTRIBUTOR, RESTRICTED_CONTRIBUTOR, BASE_CHECKER, BASE_USER, CONTRIBUTOR_PLUS]} />
					} />

					<Route path='/export-structure' element={
						<PrivateRoute component={DashboardContainer} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/rebuild-categories' element={
						<PrivateRoute 
							component={RebuildCategories} 
							forbiddenRoles={[MANAGER_PLUS, MANAGER, SUPERVISOR, RESTRICTED_CONTRIBUTOR, CONTRIBUTOR, BASE_CHECKER, BASE_USER, CONTRIBUTOR_PLUS]} />
					} />

					<Route path='/document-number-availability-check' element={
						<PrivateRoute component={NumberAvailabilityCheck} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/document-number-system-setup' element={
						<PrivateRoute component={DocumentNumberType} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/document-number-components' element={
						<PrivateRoute component={NumberTypeField} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/document-number-generator' element={
						<PrivateRoute component={NumberTypeGenerator} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/rename-document' element={
						<PrivateRoute component={RenameDocument} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/batch-file-rename' element={
						<PrivateRoute component={RenameBatchFile} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/check-for-locked-pdfs' element={
						<PrivateRoute 
							component={CheckForLockedPdfs} 
							forbiddenRoles={[MANAGER_PLUS, MANAGER, SUPERVISOR, CONTRIBUTOR, RESTRICTED_CONTRIBUTOR, BASE_CHECKER, BASE_USER, CONTRIBUTOR_PLUS]} />
					} />

					<Route path='/check-files' element={
						<PrivateRoute 
							component={CheckFiles} 
							forbiddenRoles={[MANAGER_PLUS, MANAGER, SUPERVISOR, CONTRIBUTOR, RESTRICTED_CONTRIBUTOR, BASE_CHECKER, BASE_USER, CONTRIBUTOR_PLUS]} />
					} />

					<Route path='/secret-migration' element={
						<PrivateRoute
							component={SecretMigration}
							forbiddenRoles={[MANAGER_PLUS, MANAGER, SUPERVISOR, CONTRIBUTOR, RESTRICTED_CONTRIBUTOR, BASE_CHECKER, BASE_USER, CONTRIBUTOR_PLUS]} />
					} />

					<Route path='/revision-roll-back' element={
						<PrivateRoute 
							component={RevisionRollback} 
							forbiddenRoles={[CONTRIBUTOR, RESTRICTED_CONTRIBUTOR, BASE_CHECKER, BASE_USER, CONTRIBUTOR_PLUS]} />
					} />

					<Route path='/deleted-documents' element={
						<PrivateRoute 
							component={DeletedDocuments} 
							forbiddenRoles={[CONTRIBUTOR, RESTRICTED_CONTRIBUTOR, BASE_CHECKER, BASE_USER, CONTRIBUTOR_PLUS]} />
					} />

					<Route path='/site-change' element={
						<PrivateRoute 
							component={DashboardContainer} 
							forbiddenRoles={[CONTRIBUTOR, RESTRICTED_CONTRIBUTOR, BASE_CHECKER, BASE_USER, CONTRIBUTOR_PLUS]} />
					} />

					<Route path='/reports' element={
						<PrivateRoute component={ReportComponent} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/reports/:reportType' element={
						<PrivateRoute component={ReportComponent} forbiddenRoles={[BASE_USER]} />
					} />

					<Route path='/staff' element={
						<PrivateRoute 
							component={StaffContainer} 
							forbiddenRoles={[MANAGER_PLUS, MANAGER, SUPERVISOR, RESTRICTED_CONTRIBUTOR, CONTRIBUTOR, BASE_CHECKER, BASE_USER, CONTRIBUTOR_PLUS]} />
					} />

					<Route path='/accounts' element={
						<PrivateRoute 
							component={AccountsContainer} 
							forbiddenRoles={[RESTRICTED_CONTRIBUTOR, CONTRIBUTOR, BASE_CHECKER, BASE_USER, CONTRIBUTOR_PLUS]} />
					} />

					<Route path='/equipment-types' element={
						<PrivateRoute 
							component={CriticalEquipmentType} 
							forbiddenRoles={[MANAGER_PLUS, MANAGER, SUPERVISOR, RESTRICTED_CONTRIBUTOR, CONTRIBUTOR, BASE_CHECKER, BASE_USER, CONTRIBUTOR_PLUS]} />
					} />

					<Route path='/my-profile' element={
						<PrivateRoute component={MyProfile} />
					} />

					<Route path='/release-notes' element={
						<PrivateRoute component={ReleaseNotes} />
					} />

					<Route path='/about' element={
						<PrivateRoute component={ReleaseNotes} />
					} />

					<Route path='/company' element={
						<PrivateRoute component={ReleaseNotes} />
					} />

					<Route path='/roadmap' element={
						<PrivateRoute component={Roadmap} />
					} />

					<Route path='/contact-support' element={
						<PrivateRoute component={ContactSupport} />
					} />

					<Route path='/contact-us' element={
						<PrivateRoute component={ContactSupport} />
					} />

					<Route path='/suggest-improvement' element={
						<PrivateRoute component={SuggestImprovement} />
					} />

					<Route path='/about-drawing-managers' element={
						<PrivateRoute component={AboutDrawingManagers} />
					} />

					<Route path='/privacy-policy' element={
						<PrivateRoute component={PrivacyPolicy} />
					} />

					<Route path='/terms-of-use' element={
						<PrivateRoute component={TermsOfUse} />
					} />

					<Route path='/terms-and-conditions' element={
						<PrivateRoute component={TermsOfUse} />
					} />

					<Route path='/cart-management' element={
						<PrivateRoute component={CartManagement} />
					} />

					<Route path='/help' element={
						<PrivateRoute component={HelpPage} />
					} />

					<Route path='/upload-document' element={
						<PrivateRoute component={UploadDocuments} forbiddenRoles={[BASE_USER, BASE_CHECKER]} />
					} />

					<Route path='/login' element={<LoginComponent />} />
					<Route path='/license' element={<LicenseComponent />} />
					<Route path='/error-page' element={<ErrorComponent />} />
					<Route path='*' element={<ErrorComponent notFound />} />
				</Routes>
			</>
		);
	};
 
	const rootElement = document.getElementById("root");
	const rootCacheKey = "__fifiDocsReactRoot__";
	const root = globalThis[rootCacheKey] ?? createRoot(rootElement);

	globalThis[rootCacheKey] = root;
 
	root.render(
		<Provider store={store}>
			<ConfigProvider>
				<DmsThemeProvider>
					<AntdApp>
						<AntdAppBridge />
						<BrowserRouter>
							<SystemOutageExperience>
								{/* Covers lazy routes rendered outside PrivateRoute: /login, /license, /error-page */}
								<Suspense fallback={<Spin percent='auto' fullscreen />}>
									<PdfRestrictionsProvider><AppRoutes /></PdfRestrictionsProvider>
								</Suspense>
							</SystemOutageExperience>
						</BrowserRouter>
					</AntdApp>
				</DmsThemeProvider>
			</ConfigProvider>
		</Provider>
	);
